"use client"

import { useState, useMemo, useCallback, memo } from "react"
import type { HeroInventory, ItemDefinition } from "@/types/item.types"
import { ShopItemCard } from "@/components/ui/item-card"
import { getShopWindowItems, getRerollableShopItems } from "@/data/item-types"
import { hasInventorySpace, canStackItem, getCloneChargeCostByLevel } from "@/systems/item-system"
import { playBuySound } from "@/hooks/use-sound-effects"

interface ShopWindowItemsProps {
  money: number
  inventory: HeroInventory
  heroLevel: number
  onBuyItem: (itemDefId: string, price: number) => void
  onBuyCloneCharge: (cost: number) => void
  onSpendGold: (amount: number) => void
  isVisible?: boolean // Controls staggered appearance animation
  isMobile?: boolean
}

export const ShopWindowItems = memo(function ShopWindowItems({
  money,
  inventory,
  heroLevel,
  onBuyItem,
  onBuyCloneCharge,
  onSpendGold,
  isVisible = true,
  isMobile = false,
}: ShopWindowItemsProps) {
  // Calculate clone charge cost based on hero level
  const cloneChargeCost = useMemo(() => getCloneChargeCostByLevel(heroLevel), [heroLevel])
  const [items, setItems] = useState<ItemDefinition[]>(() => getShopWindowItems())
  const [refreshCount, setRefreshCount] = useState(0)
  const [isSpinning, setIsSpinning] = useState(false)
  const [soldItems, setSoldItems] = useState<Set<string>>(new Set())

  // Calculate refresh cost
  const refreshCost = useMemo(() => {
    if (refreshCount === 0) return Math.floor(money * 0.1)
    if (refreshCount === 1) return Math.floor(money * 0.2)
    return 0
  }, [money, refreshCount])

  const canRefresh = refreshCount < 2 && money >= refreshCost && !isSpinning

  const handleRefresh = useCallback(() => {
    if (refreshCount >= 2 || money < refreshCost || isSpinning) return

    // Deduct gold
    onSpendGold(refreshCost)

    // Start spin animation
    setIsSpinning(true)

    // After animation, refresh only non-clone-charge items
    setTimeout(() => {
      setItems(prev => {
        const [equipment, consumable] = getRerollableShopItems()
        // Keep clone charge at index 0, only refresh equipment and consumable slots
        // If no item available, keep the previous item in that slot
        return [
          prev[0],
          equipment || prev[1],
          consumable || prev[2],
        ]
      })
      // Clear sold items for non-clone-charge items (clone-charge is never marked sold)
      setSoldItems(new Set())
      setIsSpinning(false)
      setRefreshCount((prev) => prev + 1)
    }, 600) // Animation duration
  }, [refreshCount, money, refreshCost, isSpinning, onSpendGold])

  const handleBuyItem = useCallback((item: ItemDefinition) => {
    if (isSpinning) return

    // Clone charge has special handling - don't add to inventory, just increment beacon charges
    // It remains available with increasing price (not marked as sold)
    if (item.id === "clone-charge") {
      if (money >= cloneChargeCost) {
        playBuySound()
        onBuyCloneCharge(cloneChargeCost)
        // Don't mark as sold - clone charge remains available with increasing price
      }
      return
    }

    // Regular item purchase
    const canPickup = hasInventorySpace(inventory) || canStackItem(inventory, item.id)
    if (money >= item.cost && canPickup) {
      playBuySound()
      onBuyItem(item.id, item.cost)
      setSoldItems((prev) => new Set(prev).add(item.id))
    }
  }, [isSpinning, money, cloneChargeCost, onBuyCloneCharge, inventory, onBuyItem])

  // Card dimensions: 20% smaller on mobile (80% of original)
  const cardWidth = isMobile ? 97 : 121
  const cardHeight = isMobile ? 160 : 200

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20">
      {/* Refresh Button - top right */}
      <div className="absolute top-2 right-2 flex flex-col items-end gap-2 pointer-events-auto z-10">
        <button
          onClick={handleRefresh}
          disabled={!canRefresh}
          className={`
            flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold
            transition-all duration-200
            ${canRefresh
              ? "bg-amber-600/80 hover:bg-amber-500/80 text-white border border-amber-400/50"
              : "bg-gray-700/50 text-gray-500 border border-gray-600/50 cursor-not-allowed"}
          `}
          title={refreshCount >= 2 ? "No more refreshes" : `Refresh items (${refreshCost}g)`}
        >
          <svg
            className={`w-3 h-3 ${isSpinning ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>{refreshCost}</span>
          <img
            src="/images/icon-coins.png"
            alt="gold"
            className="w-3 h-3"
            style={{ imageRendering: "pixelated" }}
          />
          {refreshCount < 2 && (
            <span className="text-[8px] opacity-70">({2 - refreshCount} left)</span>
          )}
        </button>
      </div>

      {/* Item Cards Grid - moved down with staggered animation */}
      <div className="flex gap-2 pointer-events-auto mt-[117px]">
        {items.map((item, index) => {
          const isSold = soldItems.has(item.id)
          const isCloneCharge = item.id === "clone-charge"
          // Clone charge uses dynamic cost based on beacon charges
          const itemPrice = isCloneCharge ? cloneChargeCost : item.cost
          const canAfford = money >= itemPrice
          const canPickup = isCloneCharge || hasInventorySpace(inventory) || canStackItem(inventory, item.id)

          // Clone charge is never marked as sold - it remains available with increasing price
          if (isSold && !isCloneCharge) {
            // Sold out placeholder
            return (
              <div
                key={`sold-${index}`}
                className={`flex items-center justify-center bg-slate-900/80 rounded-md border-2 border-slate-700/50 transition-all duration-300 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
                }`}
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  transitionDelay: isVisible ? `${index * 100}ms` : `${(items.length - 1 - index) * 50}ms`,
                }}
              >
                <span className={`text-slate-500 font-bold ${isMobile ? "text-[10px]" : "text-xs"}`}>SOLD</span>
              </div>
            )
          }

          return (
            <div
              key={`${item.id}-${index}`}
              className={`transition-all duration-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
              }`}
              style={{
                transitionDelay: isVisible ? `${index * 100}ms` : `${(items.length - 1 - index) * 50}ms`,
              }}
            >
              <ShopItemCard
                itemDef={item}
                price={itemPrice}
                canAfford={canAfford}
                canPickup={canPickup}
                onBuy={() => handleBuyItem(item)}
                isSpinning={isSpinning}
                spinDelay={index * 100}
                isMobile={isMobile}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
})
